import { util as sdkUtil } from '@vansite/ts-sharetribe-flex-sdk';
import dayjs from '../util/dayjs';

import { checkPassVoucher } from './passVoucher';
import { createVerificationCode, verifyVerificationCode } from '../util/api';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { logError, logSetUserId } from '../util/log';
import { syncPartnerRefToUser } from '../util/partnerRefSync';
import { TRANSITION_EXPIRE_PAYMENT_INTENT, TRANSITION_PAYMENT_EXPIRE } from '../util/transaction';
import {
  TRANSITION_ACCEPT,
  TRANSITION_AUTO_ACCEPT,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_UNSTUCK_CONFIRM_PAYMENT,
  txIsAutoAccepted,
  txIsCustomerCanceled as default_txIsCustomerCanceled,
  txIsDeclined,
  txIsDelivered,
  txIsOperatorCanceled as default_txIsOperatorCanceled,
  txIsProviderCanceled as default_txIsProviderCanceled,
  txIsRequested,
  txIsReviewed,
  txIsReviewedByCustomer,
  txIsReviewedByProvider,
} from '../util/transaction.default';
import {
  txIsCustomerCanceled as delayed_txIsCustomerCanceled,
  txIsFailedPayment,
  txIsOperatorCanceled as delayed_txIsOperatorCanceled,
  txIsPendingBooking,
  txIsProviderCanceled as delayed_txIsProviderCanceled,
} from '../util/transaction.delayed';
import { authInfo } from './auth.duck';
import { fillCurrentBookmark } from './bookmarks.duck';
import { addMarketplaceEntities } from './marketplaceData.duck';
import { initSetTackingUserId } from './startUp.duck';
import { fetchStripeAccount, stripeAccountCreateSuccess } from './stripeConnectAccount.duck';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const UPDATE_CURRENT_USER_PROFILE_REQUEST = 'app/user/UPDATE_CURRENT_USER_PROFILE';
export const UPDATE_CURRENT_USER_PROFILE_SUCCESS = 'app/user/UPDATE_CURRENT_USER_PROFILE_SUCCESS';
export const UPDATE_CURRENT_USER_PROFILE_ERROR = 'app/user/UPDATE_CURRENT_USER_PROFILE_ERROR';

export const EXPIRED_ORDER_MODAL_OPEN = 'app/user/EXPIRED_ORDER_MODAL_OPEN';
export const EXPIRED_ORDER_MODAL_CLOSE = 'app/user/EXPIRED_ORDER_MODAL_CLOSE';

export const PHONE_NUMBER_VERIFICATION_CODE_REQUEST =
  'app/user/PHONE_NUMBER_VERIFICATION_CODE_REQUEST';
export const PHONE_NUMBER_VERIFICATION_CODE_SUCCESS =
  'app/user/PHONE_NUMBER_VERIFICATION_CODE_SUCCESS';
export const PHONE_NUMBER_VERIFICATION_CODE_ERROR = 'app/user/PHONE_NUMBER_VERIFICATION_CODE_ERROR';

export const PHONE_NUMBER_VERIFICATION_REQUEST = 'app/user/PHONE_NUMBER_VERIFICATION_REQUEST';
export const PHONE_NUMBER_VERIFICATION_SUCCESS = 'app/user/PHONE_NUMBER_VERIFICATION_SUCCESS';
export const PHONE_NUMBER_VERIFICATION_ERROR = 'app/user/PHONE_NUMBER_VERIFICATION_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, ...oldRelationships, ...relationships, attributes };
};

/** @type {import('../../types/vansite').VanSite.State.User} */
const initialState = {
  currentUser: null,
  currentUserInProgress: false,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: { provider: 0, customer: 0 },
  currentUserNotificationCountError: null,
  currentUserOpenBookings: [],
  currentUserHasOrders: false, // This is not fetched unless unverified emails exist
  currentUserOpenOrdersCount: 0, // This is not fetched unless unverified emails exist
  currentUserHasOrdersInProgress: false,
  currentUserExpiredOrder: null,
  currentUserExpiredOrderModalOpen: false,
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  customerTransactionsCount: 0,
  phoneNumberSendVerificationCodeInProgress: false,
  phoneNumberSendVerificationCodeError: null,
  phoneNumberCodeVerificationInProgress: false,
  phoneNumberCodeVerificationError: null,
};

/**
 *
 * @param state
 * @param action
 */
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null, currentUserInProgress: true };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload), currentUserInProgress: false };
    case CURRENT_USER_SHOW_ERROR:
      return { ...state, currentUserShowError: payload, currentUserInProgress: false };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: { provider: 0, customer: 0 },
        currentUserNotificationCountError: null,
        currentUserInProgress: false,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        currentUserNotificationCount: {
          provider: payload.providerTransactions.length,
          customer: payload.customerTransactions.length,
        },
        customerTransactionsCount: payload.customerTransactionsCount,
      };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersInProgress: true, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return {
        ...state,
        currentUserOpenBookings: payload.openOrders,
        currentUserHasOrders: payload.hasOrders,
        currentUserOpenOrdersCount: payload.openOrdersCount,
        currentUserExpiredOrder: payload.expiredOrder,
        currentUserHasOrdersInProgress: false,
      };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      return {
        ...state,
        currentUserHasOrdersInProgress: false,
        currentUserHasOrdersError: payload,
      };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case UPDATE_CURRENT_USER_PROFILE_REQUEST:
      return { ...state };
    case UPDATE_CURRENT_USER_PROFILE_SUCCESS:
      return { ...state };
    case UPDATE_CURRENT_USER_PROFILE_ERROR:
      return { ...state };

    case EXPIRED_ORDER_MODAL_OPEN:
      return { ...state, currentUserExpiredOrderModalOpen: true };
    case EXPIRED_ORDER_MODAL_CLOSE:
      return { ...state, currentUserExpiredOrderModalOpen: false };

    case PHONE_NUMBER_VERIFICATION_CODE_REQUEST:
      return {
        ...state,
        phoneNumberSendVerificationCodeInProgress: true,
        phoneNumberSendVerificationCodeError: null,
        phoneNumberCodeVerificationError: null,
      };
    case PHONE_NUMBER_VERIFICATION_CODE_SUCCESS:
      return { ...state, phoneNumberSendVerificationCodeInProgress: false };
    case PHONE_NUMBER_VERIFICATION_CODE_ERROR:
      return {
        ...state,
        phoneNumberSendVerificationCodeInProgress: false,
        phoneNumberSendVerificationCodeError: payload,
      };

    case PHONE_NUMBER_VERIFICATION_REQUEST:
      return {
        ...state,
        phoneNumberCodeVerificationInProgress: true,
        phoneNumberCodeVerificationError: null,
      };
    case PHONE_NUMBER_VERIFICATION_SUCCESS:
      return { ...state, phoneNumberCodeVerificationInProgress: false };
    case PHONE_NUMBER_VERIFICATION_ERROR:
      return {
        ...state,
        phoneNumberCodeVerificationInProgress: false,
        phoneNumberCodeVerificationError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = (state) => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = (state) => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = (user) => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = (e) => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (payload) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload,
});

const fetchCurrentUserHasListingsError = (e) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = (
  providerTransactions,
  customerTransactions,
  customerTransactionsCount,
) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { providerTransactions, customerTransactions, customerTransactionsCount },
});

const fetchCurrentUserNotificationsError = (e) => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});
export const fetchCurrentUserHasOrdersSuccess = (payload) => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: payload,
});
const fetchCurrentUserHasOrdersError = (e) => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});
export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});
export const sendVerificationEmailError = (e) => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const updateCurrentUserProfileRequest = () => ({
  type: UPDATE_CURRENT_USER_PROFILE_REQUEST,
});
export const updateCurrentUserProfileSuccess = (result) => ({
  type: UPDATE_CURRENT_USER_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateCurrentUserProfileError = (e) => ({
  type: UPDATE_CURRENT_USER_PROFILE_ERROR,
  error: true,
  payload: e,
});

export const expiredOrderModalOpen = () => ({ type: EXPIRED_ORDER_MODAL_OPEN });
export const expiredOrderModalClose = () => ({ type: EXPIRED_ORDER_MODAL_CLOSE });

export const phoneNumberVerificationCodeRequest = () => ({
  type: PHONE_NUMBER_VERIFICATION_CODE_REQUEST,
});
export const phoneNumberVerificationCodeSuccess = () => ({
  type: PHONE_NUMBER_VERIFICATION_CODE_SUCCESS,
});
export const phoneNumberVerificationCodeError = (error) => ({
  type: PHONE_NUMBER_VERIFICATION_CODE_ERROR,
  payload: error,
  error: true,
});

export const phoneNumberVerificationRequest = () => ({ type: PHONE_NUMBER_VERIFICATION_REQUEST });
export const phoneNumberVerificationSuccess = () => ({ type: PHONE_NUMBER_VERIFICATION_SUCCESS });
export const phoneNumberVerificationError = (error) => ({
  type: PHONE_NUMBER_VERIFICATION_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    per_page: 1,
  };

  return sdk.ownListings
    .query(params)
    .then((response) => {
      const hasListings = response?.data?.data?.length > 0;
      dispatch(
        fetchCurrentUserHasListingsSuccess({
          hasListings: !!hasListings,
        }),
      );
    })
    .catch((e) => {
      dispatch(fetchCurrentUserHasListingsError(storableError(e)));
      logError(e, 'fetch-current-user-has-listings-failed');
    });
};

export const fetchCurrentUserHasOrders = () => async (dispatch, getState, sdk) => {
  const { currentUser, currentUserHasOrdersInProgress } = getState().user;
  if (currentUserHasOrdersInProgress) {
    return Promise.resolve(null);
  }

  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!currentUser) {
    dispatch(
      fetchCurrentUserHasOrdersSuccess({ hasOrders: false, openOrdersCount: 0, expiredOrder: null, openOrders: [] }),
    );
    return Promise.resolve(null);
  }

  try {
    const [openTransactionsResponse, expiredTransactionsResponse] = await Promise.all([
      sdk.transactions.query({
        only: 'order',
        page: 1,
        include: ['booking'],
        lastTransitions: [
          TRANSITION_ACCEPT,
          TRANSITION_AUTO_ACCEPT,
          TRANSITION_CONFIRM_PAYMENT,
          TRANSITION_UNSTUCK_CONFIRM_PAYMENT,
        ],
        bookingEnd: `${dayjs.utc().endOf('day').toISOString()},${dayjs.utc().add(1, 'year').endOf('day').toISOString()}`,
      }),
      sdk.transactions.query({
        only: 'order',
        page: 1,
        lastTransitions: [TRANSITION_EXPIRE_PAYMENT_INTENT, TRANSITION_PAYMENT_EXPIRE],
      }),
    ]);

    const { data: transactions = [], included = [] } = openTransactionsResponse.data;
    const hasOrders = transactions.length > 0;
    const openOrders = transactions;

    // data.data can be missing on a mangled response (VANSITE-FRONTEND-4KV).
    const expiredOrder = (expiredTransactionsResponse.data?.data ?? [])
      // sort for createdAt
      .find((tx) =>
        [TRANSITION_EXPIRE_PAYMENT_INTENT, TRANSITION_PAYMENT_EXPIRE].includes(
          tx.attributes.lastTransition,
        ),
      );

    if (expiredOrder && !expiredOrder.attributes.metadata.customerNotificationModalShown) {
      dispatch(expiredOrderModalOpen());
    }

    const result = {
      hasOrders: !!hasOrders,
      openOrdersCount: openOrders.length,
      openOrders: openOrders.map((tx) => included.find((b) => b.id.uuid === tx.relationships.booking.data.id.uuid)),
      expiredOrder: expiredOrder,
    };

    dispatch(addMarketplaceEntities(openTransactionsResponse));
    dispatch(fetchCurrentUserHasOrdersSuccess(result));
    return result;
  } catch (e) {
    logError(e, 'fetch-current-user-has-orders-failed');
    dispatch(fetchCurrentUserHasOrdersError(storableError(e)));
  }
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => async (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserNotificationsRequest());

  const queryParams = {
    page: 1,
    per_page: NOTIFICATION_PAGE_SIZE,
  };

  try {
    const [customerTransactionsResponse, providerTransactionsResponse] = await Promise.all([
      sdk.transactions.query({
        ...queryParams,
        only: 'order',
      }),
      sdk.transactions.query({
        ...queryParams,
        only: 'sale',
      }),
    ]);

    dispatch(addMarketplaceEntities(customerTransactionsResponse));
    dispatch(addMarketplaceEntities(providerTransactionsResponse));

    const filterTransactions = (transactions, notificationKey, conditions) => {
      return transactions.filter((tx) => {
        const notification = tx.attributes.metadata[notificationKey];
        if (notification) return false;

        return conditions.some((condition) => condition(tx));
      });
    };

    const customerConditions = [
      txIsDeclined,
      txIsFailedPayment,
      default_txIsProviderCanceled,
      delayed_txIsProviderCanceled,
      txIsDelivered,
      (tx) => txIsReviewedByProvider(tx) && !txIsReviewed(tx),
    ];

    const providerConditions = [
      txIsRequested,
      txIsPendingBooking,
      txIsAutoAccepted,
      default_txIsCustomerCanceled,
      delayed_txIsCustomerCanceled,
      default_txIsOperatorCanceled,
      delayed_txIsOperatorCanceled,
      txIsDelivered,
      (tx) => txIsReviewedByCustomer(tx) && !txIsReviewed(tx),
    ];

    const customerTransactions = filterTransactions(
      customerTransactionsResponse.data.data,
      'customerNotification',
      customerConditions,
    );
    const providerTransactions = filterTransactions(
      providerTransactionsResponse.data.data,
      'providerNotification',
      providerConditions,
    );

    dispatch(
      fetchCurrentUserNotificationsSuccess(
        providerTransactions,
        customerTransactions,
        customerTransactionsResponse.data.data.length,
      ),
    );
  } catch (e) {
    dispatch(fetchCurrentUserNotificationsError(storableError(e)));
    logError(e, 'fetch-current-user-notifications-failed');
  }
};

export const fetchCurrentUser =
  (params = null) =>
    (dispatch, getState, sdk) => {
      dispatch(currentUserShowRequest());
      const { isAuthenticated } = getState().auth;

      if (!isAuthenticated) {
        // Make sure current user is null
        dispatch(currentUserShowSuccess(null));
        return Promise.resolve({});
      }

      const parameters = params || {
        include: ['profileImage', 'stripeAccount'],
        'fields.image': [
          'variants.square-small',
          'variants.square-small2x',
          'variants.square-xsmall',
          'variants.square-xsmall2x',
        ],
        'imageVariant.square-xsmall': sdkUtil.objectQueryString({
          w: 40,
          h: 40,
          fit: 'crop',
        }),
        'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
          w: 80,
          h: 80,
          fit: 'crop',
        }),
      };

      let currentUserObj = null;

      return sdk.currentUser
        .show(parameters)
        .then((response) => {
          const entities = denormalisedResponseEntities(response);
          if (entities.length !== 1) {
            throw new Error('Expected a resource in the sdk.currentUser.show response');
          }
          const currentUser = entities[0];

          // Save stripeAccount to store.stripe.stripeAccount if it exists
          if (currentUser.stripeAccount) {
            dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
          }

          // set current user id to the logger
          logSetUserId(currentUser.id.uuid);
          dispatch(currentUserShowSuccess(currentUser));
          currentUserObj = currentUser;
          return currentUser;
        })
        .then((currentUser) => {
          dispatch(fetchCurrentUserHasListings());
          dispatch(fetchCurrentUserNotifications());
          dispatch(fetchCurrentUserHasOrders());
          dispatch(fetchStripeAccount());
          dispatch(checkPassVoucher());
          dispatch(initSetTackingUserId());

          const bookmarks = currentUser.attributes.profile.privateData.bookmarks;
          dispatch(fillCurrentBookmark(bookmarks ? bookmarks : []));

          dispatch(addMissingDataToCurrentUser());

          // Partner-Ref (Haspa) nachtragen — fire-and-forget, blockiert nie.
          syncPartnerRefToUser(currentUser);
          // Make sure auth info is up to date
          dispatch(authInfo());
        })
        .then(() => {
          return currentUserObj;
        })
        .catch((e) => {
          // Make sure auth info is up to date
          dispatch(authInfo());
          dispatch(currentUserShowError(storableError(e)));
          logError(e, 'fetch-current-user-failed');
        });
    };

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch((e) => dispatch(sendVerificationEmailError(storableError(e))));
};

export const updateUserProfile = (params) => async (dispatch, getState, sdk) => {
  dispatch(updateCurrentUserProfileRequest());

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  try {
    const response = await sdk.currentUser.updateProfile(params, queryParams);
    dispatch(updateCurrentUserProfileSuccess(response));

    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    const currentUser = entities[0];

    // Update current user in state.user.currentUser through user.duck.js
    dispatch(currentUserShowSuccess(currentUser));
    return currentUser;
  } catch (e) {
    dispatch(updateCurrentUserProfileError(e));
  }
};

export const createPhoneNumberVerificationCode = (phoneNumber) => async (dispatch, getState) => {
  const { phoneNumberSendVerificationCodeInProgress } = getState().user;
  if (phoneNumberSendVerificationCodeInProgress) {
    return;
  }

  dispatch(phoneNumberVerificationCodeRequest());
  try {
    const response = await createVerificationCode({ phoneNumber });
    if (response.data.valid === false) {
      dispatch(phoneNumberVerificationCodeError(response.data));
    } else {
      dispatch(phoneNumberVerificationCodeSuccess());
    }
    return response.data;
  } catch (e) {
    logError(e, 'create-phone-number-verification-code');
    dispatch(phoneNumberVerificationCodeError(storableError(e)));
    throw e; // throw error
  }
};

/**
 * @param phoneNumber
 * @param code
 * @returns {(dispatch: Dispatch, getState: GetState, sdk, SharetribeSDK) => Promise<void>}
 */
export const verifyPhoneNumberVerificationCode =
  (phoneNumber, code) => async (dispatch, getState) => {
    const { phoneNumberCodeVerificationInProgress } = getState().user;
    if (phoneNumberCodeVerificationInProgress) {
      return;
    }

    dispatch(phoneNumberVerificationRequest());
    try {
      const response = await verifyVerificationCode({ phoneNumber, code });
      // The backend has occasionally returned an empty body — treat a missing
      // `data` payload as an invalid code rather than crashing the form.
      const { valid } = response?.data ?? {};
      if (!valid) {
        dispatch(
          phoneNumberVerificationError({
            code: 'invalid-verification-code',
          }),
        );
      } else {
        dispatch(phoneNumberVerificationSuccess());
      }
      return response.data;
    } catch (e) {
      if (e.status === 404) {
        // Twilio deletes the VerificationCheck after expiry/max attempts/approval,
        // so a 404 here is an expected outcome, not a real error — skip logError.
        dispatch(
          phoneNumberVerificationError({
            code: 'expired-verification-code',
          }),
        );
      } else {
        logError(e, 'verify-phone-number-verification-code');
        dispatch(phoneNumberVerificationError(e));
      }
      throw e; // throw error to stop signup process
    }
  };

/**
 * Add missing data to current user if needed
 * @returns {(dispatch: Dispatch, getState: GetState, sdk, SharetribeSDK) => Promise<void>}
 */
export const addMissingDataToCurrentUser = () => async (dispatch, getState, sdk) => {
  try {
    const currentUser = getState().user.currentUser;
    if (!currentUser) {
      return;
    }

    const { phoneNumber } = currentUser.attributes.profile.protectedData;
    const { language } = currentUser.attributes.profile.privateData;

    const browserLanguage = navigator.language.split('-')[0];
    if (!language) {
      dispatch(updateUserProfile({ privateData: { language: browserLanguage.toLowerCase() } }));
    }
    if (phoneNumber) {
      const { isValidNumber, parsePhoneNumber } = await import('libphonenumber-js'); // Dynamic import for lazy loading
      if (!isValidNumber(phoneNumber)) {
        const parsedPhoneNumber = parsePhoneNumber(phoneNumber, browserLanguage.toUpperCase());
        dispatch(
          updateUserProfile({
            protectedData: { phoneNumber: parsedPhoneNumber.number },
          }),
        );
      }
    }
  } catch (e) {
    logError(e, 'add-missing-data-to-current-user');
  }
};
